/**
 * TopicBar — pinned room-topic banner shown above the message list when
 * a mod has set one. Renders the legacy-HTML subset safely so font/b/i
 * tags in topics work.
 *
 * Mods + admins see an "edit" pencil affordance.
 */
import { useState } from 'react';
import { hasLegacyHtml, renderLegacyHtml } from '../lib/legacyHtml.js';
import { autolink } from '../lib/autolink.js';

interface Props {
    room: string;
    topic: string;
    setBy: string;
    isMod: boolean;
    onSetTopic: (newTopic: string) => void;
}

export function TopicBar({ topic, setBy, isMod, onSetTopic }: Props) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft]     = useState(topic);

    if (!topic && !editing && !isMod) return null;

    if (editing) {
        return (
            <div className="topic-bar topic-bar-editing">
                <span className="topic-bar-label">Topic:</span>
                <input
                    type="text"
                    className="topic-bar-input"
                    value={draft}
                    maxLength={500}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter')  { onSetTopic(draft.trim()); setEditing(false); }
                        if (e.key === 'Escape') { setDraft(topic); setEditing(false); }
                    }}
                    autoFocus
                />
                <button className="topic-bar-btn" onClick={() => { onSetTopic(draft.trim()); setEditing(false); }}>Save</button>
                <button className="topic-bar-btn" onClick={() => { setDraft(topic); setEditing(false); }}>Cancel</button>
                {topic && (
                    <button className="topic-bar-btn topic-bar-btn-danger"
                            onClick={() => { onSetTopic(''); setEditing(false); }}>Clear</button>
                )}
            </div>
        );
    }

    return (
        <div className="topic-bar" title={setBy ? `Set by ${setBy}` : undefined}>
            <span className="topic-bar-label">Topic:</span>
            <span className="topic-bar-text">
                {topic
                    ? hasLegacyHtml(topic) ? renderLegacyHtml(topic) : autolink(topic)
                    : <em className="topic-bar-empty">(no topic — click ✎ to set one)</em>}
            </span>
            {isMod && (
                <button className="topic-bar-edit"
                        onClick={() => { setDraft(topic); setEditing(true); }}
                        title="Edit topic">✎</button>
            )}
        </div>
    );
}
